import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { fiscalPeriodsApi } from '@/services/fiscalPeriodService';
import type {
  FiscalPeriod,
  FiscalPeriodFilters,
  FiscalPeriodCreatePayload,
  CloseFiscalPeriodPayload,
  ReopenFiscalPeriodPayload,
  ClosingEntryPreview,
  PeriodEndValidationResult,
} from '@/types/accounting';

interface FiscalPeriodApiError {
  response?: { data?: { message?: string } };
  status?: number;
  message?: string;
}

/**
 * Typed error normalizer: api.ts re-rejects handled statuses as a plain
 * `{ ...body, status }` object (no `response` wrapper), so read the message
 * from either shape. Returns a plain serializable string so reducers/UI can
 * render `state.error` reliably.
 */
export function extractFiscalPeriodError(error: unknown): string {
  const err = error as FiscalPeriodApiError;
  return err?.response?.data?.message ?? err?.message ?? 'Fiscal period request failed';
}

/** Last filters the list was loaded with, reused by post-mutation refetches. */
const currentPeriodFilters = (
  getState: () => unknown
): FiscalPeriodFilters | undefined =>
  (getState() as { fiscalPeriod?: { filters?: FiscalPeriodFilters | null } }).fiscalPeriod?.filters ?? undefined;

export const fetchPeriods = createAsyncThunk(
  'fiscalPeriod/fetchPeriods',
  async (params: FiscalPeriodFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await fiscalPeriodsApi.getAll(params || {});
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const fetchCurrentPeriod = createAsyncThunk(
  'fiscalPeriod/fetchCurrentPeriod',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fiscalPeriodsApi.getCurrent();
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const createPeriod = createAsyncThunk(
  'fiscalPeriod/createPeriod',
  async (data: FiscalPeriodCreatePayload, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await fiscalPeriodsApi.create(data);
            // Refetch with the filters the list is actually showing: passing
      // undefined reset the store to the unfiltered first page, so the
      // table silently stopped matching the selected filters.
      dispatch(fetchPeriods(currentPeriodFilters(getState)));
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const closePeriod = createAsyncThunk(
  'fiscalPeriod/closePeriod',
  async ({ id, payload }: { id: number | string, payload: CloseFiscalPeriodPayload }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await fiscalPeriodsApi.close(id, payload);
            // Refetch with the filters the list is actually showing: passing
      // undefined reset the store to the unfiltered first page, so the
      // table silently stopped matching the selected filters.
      dispatch(fetchPeriods(currentPeriodFilters(getState)));
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const reopenPeriod = createAsyncThunk(
  'fiscalPeriod/reopenPeriod',
  async ({ id, payload }: { id: number | string, payload: ReopenFiscalPeriodPayload }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await fiscalPeriodsApi.reopen(id, payload);
            // Refetch with the filters the list is actually showing: passing
      // undefined reset the store to the unfiltered first page, so the
      // table silently stopped matching the selected filters.
      dispatch(fetchPeriods(currentPeriodFilters(getState)));
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const fetchClosingPreview = createAsyncThunk(
  'fiscalPeriod/fetchClosingPreview',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await fiscalPeriodsApi.previewClosing(id);
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const fetchValidationChecklist = createAsyncThunk(
  'fiscalPeriod/fetchValidationChecklist',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await fiscalPeriodsApi.validationChecklist(id);
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

export const fetchPeriod = createAsyncThunk(
  'fiscalPeriod/fetchPeriod',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await fiscalPeriodsApi.getById(id);
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(extractFiscalPeriodError(error));
    }
  }
);

interface FiscalPeriodState {
  periods: FiscalPeriod[];
  /** Filters the current `periods` were loaded with (null = unfiltered). */
  filters: FiscalPeriodFilters | null;
  currentPeriod: FiscalPeriod | null;
  closingPreview: ClosingEntryPreview | null;
  validationChecklist: PeriodEndValidationResult | null;
  loading: boolean;
  error: string | null;
  totalRows: number;
}

const initialState: FiscalPeriodState = {
  periods: [],
  filters: null,
  currentPeriod: null,
  closingPreview: null,
  validationChecklist: null,
  loading: false,
  error: null,
  totalRows: 0,
};

const fiscalPeriodSlice = createSlice({
  name: 'fiscalPeriod',
  initialState,
  reducers: {
    clearCurrentPeriod(state) {
      state.currentPeriod = null;
    },
    clearClosingPreview(state) {
      state.closingPreview = null;
      state.validationChecklist = null;
    },
    clearState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: FiscalPeriodState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: FiscalPeriodState, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = typeof action.payload === 'string' ? action.payload : null;
    };

    builder
      .addCase(fetchPeriods.pending, (state, action) => {
        handlePending(state);
        state.filters = (action.meta.arg as FiscalPeriodFilters | undefined) ?? null;
      })
      .addCase(fetchPeriods.fulfilled, (state, action) => {
        state.loading = false;
        state.periods = action.payload?.periods || [];
        state.totalRows = action.payload?.totalRows || action.payload?.periods?.length || 0;
      })
      .addCase(fetchPeriods.rejected, handleRejected)

      .addCase(fetchCurrentPeriod.pending, handlePending)
      .addCase(fetchCurrentPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPeriod = action.payload;
      })
      .addCase(fetchCurrentPeriod.rejected, handleRejected)

      .addCase(fetchPeriod.pending, handlePending)
      .addCase(fetchPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPeriod = action.payload;
      })
      .addCase(fetchPeriod.rejected, handleRejected)

      // The mutation thunks dispatch `fetchPeriods` before they fulfill, and
      // that refetch owns the `loading` reset — no fulfilled handler here.
      .addCase(createPeriod.pending, handlePending)
      .addCase(createPeriod.rejected, handleRejected)

      .addCase(closePeriod.pending, handlePending)
      .addCase(closePeriod.rejected, handleRejected)

      .addCase(reopenPeriod.pending, handlePending)
      .addCase(reopenPeriod.rejected, handleRejected)

      .addCase(fetchClosingPreview.pending, handlePending)
      .addCase(fetchClosingPreview.fulfilled, (state, action) => {
        state.loading = false;
        state.closingPreview = action.payload;
      })
      .addCase(fetchClosingPreview.rejected, handleRejected)

      .addCase(fetchValidationChecklist.pending, handlePending)
      .addCase(fetchValidationChecklist.fulfilled, (state, action) => {
        state.loading = false;
        state.validationChecklist = action.payload;
      })
      .addCase(fetchValidationChecklist.rejected, handleRejected);
  },
});

export const { clearCurrentPeriod, clearClosingPreview, clearState } = fiscalPeriodSlice.actions;
export default fiscalPeriodSlice.reducer;
